import { Box, Button, Card, CardContent, CardHeader, TextField, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { CustomerDto } from '../../../interactors/gen/backendClient'
import { Save } from '@mui/icons-material'

interface Props {
  association: CustomerDto
  onSubmit: (data: Partial<CustomerDto>) => Promise<void>
}

export const BannerColorCard: React.FC<Props> = ({ association, onSubmit }) => {
  const [bannerColor, setBannerColor] = useState(association?.pageBannerColor || '#F3B700')
  const [hexInput, setHexInput] = useState(association?.pageBannerColor || '#F3B700')
  const [hexError, setHexError] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setBannerColor(association?.pageBannerColor || '#F3B700')
    setHexInput(association?.pageBannerColor || '#F3B700')
    setHasChanges(false)
  }, [association?.pageBannerColor])

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value
    setBannerColor(newColor)
    setHexInput(newColor)
    setHasChanges(newColor !== association?.pageBannerColor)
    setHexError('')
  }

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setHexInput(value)

    const hexRegex = /^#[0-9A-Fa-f]{6}$/
    if (hexRegex.test(value)) {
      setHexError('')
      setBannerColor(value)
      setHasChanges(value !== association?.pageBannerColor)
    } else {
      setHexError('Le code couleur doit être au format HEX (ex: #F3B700)')
    }
  }

  const handleSave = async () => {
    if (hexError) return

    setIsSaving(true)
    try {
      await onSubmit({ pageBannerColor: bannerColor })
      setHasChanges(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setBannerColor(association?.pageBannerColor || '#F3B700')
    setHexInput(association?.pageBannerColor || '#F3B700')
    setHasChanges(false)
    setHexError('')
  }

  return (
    <Card>
      <CardHeader title={null} sx={{ pb: 0 }} subheader={null} />
      <CardContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'stretch', lg: 'flex-start' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, lg: 2.5 },
            }}
          >
            <Box sx={{ maxWidth: { xs: '100%', lg: 320 }, pr: { lg: 1 } }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F3764', lineHeight: 1.2 }}>
                Couleur de la bannière
              </Typography>
              <Typography sx={{ mt: 0.35, fontSize: 13, color: '#7A8197', lineHeight: 1.35 }}>
                Personnalisez la couleur de la bannière sur votre page Petso.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '120px 138px 138px auto' },
                gap: 1.25,
                alignItems: 'end',
                justifyContent: { sm: 'end' },
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#7A8197' }}>Sélecteur</Typography>
                <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                  <TextField
                    type="color"
                    value={bannerColor}
                    onChange={handleColorChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      style: { height: 40, width: 88, cursor: 'pointer', padding: 0 },
                    }}
                    sx={{
                      width: 88,
                      '& .MuiInputBase-root': {
                        height: 40,
                        padding: 0,
                        borderRadius: 2.5,
                      },
                      '& .MuiInputBase-input': {
                        padding: 0,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#7A8197' }}>Code</Typography>
                <TextField
                  type="text"
                  value={hexInput}
                  onChange={handleHexInputChange}
                  error={!!hexError}
                  helperText={hexError}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 138,
                    '& .MuiInputBase-root': {
                      height: 40,
                      borderRadius: 2.5,
                    },
                    '& .MuiInputBase-input': { py: 0.8 },
                  }}
                  placeholder="#F3B700"
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#7A8197' }}>Rendu</Typography>
                <Box
                  sx={{
                    width: 138,
                    height: 40,
                    backgroundColor: bannerColor,
                    border: '1px solid rgba(122, 129, 151, 0.22)',
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#fff',
                      fontWeight: 900,
                      fontSize: 12,
                      textShadow: '0 0 4px rgba(0,0,0,0.5)',
                    }}
                  >
                    Votre bannière
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                  alignItems: 'flex-end',
                  gap: 1,
                  minHeight: 68,
                }}
              >
                {hasChanges && (
                  <Button
                    variant="text"
                    onClick={handleReset}
                    disabled={isSaving}
                    sx={{
                      height: 38,
                      borderRadius: '999px',
                      px: 1.5,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    Annuler
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<Save sx={{ fontSize: 18 }} />}
                  onClick={handleSave}
                  disabled={!hasChanges || !!hexError || isSaving}
                  sx={{
                    height: 38,
                    borderRadius: '999px',
                    px: 2.2,
                    borderColor: '#1F3764',
                    color: '#1F3764',
                    fontWeight: 800,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: '#1F3764', backgroundColor: 'rgba(31, 55, 100, 0.06)' },
                  }}
                >
                  {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
